export const createEmptyGroup = (label, sampleLog = null) => ({
  label,
  logs: [],
  totalHours: 0,
  totalEntries: 0,
  uniqueIssues: new Map(),
  uniqueAuthors: new Set(),
  uniqueAssignees: new Set(),
  uniqueProjects: new Set(),
  overEstimatedEntries: 0,
  underEstimatedEntries: 0,
  onTimeEntries: 0,
  earliestDate: null,
  latestDate: null,
  issue_summary: sampleLog?.issue_summary,
  issue_status: sampleLog?.issue_status,
  issue_assignee: sampleLog?.issue_assignee,
  issue_priority: sampleLog?.issue_priority,
  issue_project_key: sampleLog?.issue_project_key,
  issue_type: sampleLog?.issue_type,
  statusDistribution: {},
  priorityDistribution: {},
  projectDistribution: {},
  workDays: new Set(),
  avgHoursPerEntry: 0,
  avgHoursPerIssue: 0,
});

export const addWorklogToGroup = (group, log) => {
  group.logs.push(log);
  group.totalHours += log.loggedHours;
  group.totalEntries += 1;

  if (!group.issue_type && log.issue_type) {
    group.issue_type = log.issue_type;
  }

  if (!group.uniqueIssues.has(log.issue_key)) {
    group.uniqueIssues.set(log.issue_key, {
      key: log.issue_key,
      summary: log.issue_summary,
      estimatedHours: log.estimatedHours,
      status: log.issue_status,
      assignee: log.issue_assignee,
      priority: log.issue_priority,
      project: log.issue_project_key,
    });
  }

  if (log.author) group.uniqueAuthors.add(log.author);
  if (log.issue_assignee) group.uniqueAssignees.add(log.issue_assignee);
  if (log.issue_project_key) group.uniqueProjects.add(log.issue_project_key);

  if (log.variance > 0) {
    group.overEstimatedEntries += 1;
  } else if (log.variance < 0) {
    group.underEstimatedEntries += 1;
  } else {
    group.onTimeEntries += 1;
  }

  const logDate = new Date(log.loggedDate);
  if (!group.earliestDate || logDate < group.earliestDate) {
    group.earliestDate = logDate;
  }
  if (!group.latestDate || logDate > group.latestDate) {
    group.latestDate = logDate;
  }

  group.workDays.add(log.loggedDate);

  const status = log.issue_status;
  group.statusDistribution[status] = (group.statusDistribution[status] || 0) + 1;
  const priority = log.issue_priority || 'No Priority';
  group.priorityDistribution[priority] = (group.priorityDistribution[priority] || 0) + 1;
  const project = log.issue_project_key;
  group.projectDistribution[project] = (group.projectDistribution[project] || 0) + 1;
};

export const calculateFinalMetrics = (group) => {
  group.uniqueAuthors = group.uniqueAuthors.size;
  group.uniqueAssignees = group.uniqueAssignees.size;
  group.uniqueProjects = group.uniqueProjects.size;
  group.workDays = group.workDays.size;
  group.totalEstimated = Array.from(group.uniqueIssues.values())
    .reduce((sum, issue) => sum + issue.estimatedHours, 0);
  group.uniqueIssuesCount = group.uniqueIssues.size;
  group.avgHoursPerEntry = group.totalEntries > 0 ? (group.totalHours / group.totalEntries) : 0;
  group.avgHoursPerIssue = group.uniqueIssuesCount > 0 ? (group.totalHours / group.uniqueIssuesCount) : 0;
  group.efficiency = group.totalEstimated > 0 ?
    ((group.totalHours / group.totalEstimated) * 100) : 0;
  group.variance = group.totalHours - group.totalEstimated;
  if (group.earliestDate && group.latestDate) {
    const diffTime = Math.abs(group.latestDate - group.earliestDate);
    group.dateSpan = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  } else {
    group.dateSpan = 1;
  }
};

export const finalizeGroups = (grouped) => {
  Object.values(grouped).forEach(calculateFinalMetrics);
  return grouped;
};

const shouldIncludeInNoParentGroup = (log, parentKeys, parentInfoMap, validParentKeys) => {
  if (!log.issue_parent_key && !parentKeys.has(log.issue_key)) {
    return true;
  }

  if (log.issue_parent_key && !validParentKeys.has(log.issue_parent_key)) {
    if (!validParentKeys.has(log.issue_key)) {
      return true;
    }
    return false;
  }

  if (!log.issue_parent_key && parentKeys.has(log.issue_key)) {
    const parentType = parentInfoMap.get(log.issue_key)?.type?.toLowerCase() || log.issue_type?.toLowerCase();
    if (parentType === 'epic') {
      return true;
    }
  }

  return false;
};

export const groupByParent = (worklogs) => {
  const parentKeys = new Set();
  const parentInfoMap = new Map();

  // console.log(worklogs);

  worklogs.forEach(log => {
    if (!log.issue_parent_key) return;

    parentKeys.add(log.issue_parent_key);

    // Store parent info from child worklogs
    if (log.issue_parent_key && !parentInfoMap.has(log.issue_parent_key)) {
      parentInfoMap.set(log.issue_parent_key, {
        summary: log.issue_parent_summary,
        status: log.issue_parent_status,
        priority: log.issue_parent_priority,
        type: log.issue_parent_type,
        assignee: log.issue_parent_assignee,
        projectKey: log.issue_parent_project_key
      });
    }
  });

  // console.log(parentInfoMap);

  const validParentKeys = new Set();
  parentKeys.forEach(parentKey => {
    const parentInfo = parentInfoMap.get(parentKey);
    const parentType = parentInfo?.type;
    if (!parentType || (parentType.toLowerCase() !== 'epic')) {
      validParentKeys.add(parentKey);
    }
  });

  const grouped = worklogs.reduce((acc, log) => {
    if (!log.issue_parent_key) {
      return acc;
    }

    if (!validParentKeys.has(log.issue_parent_key)) {
      return acc;
    }

    const groupKey = log.issue_parent_key;
    const groupLabel = `${groupKey}`;

    if (!acc[groupKey]) {
      const parentInfo = parentInfoMap.get(groupKey);
      if (!parentInfo) {
        return acc;
      }

      const sampleLog = {
        issue_summary: parentInfo.summary,
        issue_status: parentInfo.status,
        issue_priority: parentInfo.priority,
        issue_type: parentInfo.type,
        issue_project_key: parentInfo.projectKey,
        issue_assignee: parentInfo.assignee
      };
      
      acc[groupKey] = createEmptyGroup(groupLabel, sampleLog);
      acc[groupKey].parentType = parentInfo.type || null;
    }

    addWorklogToGroup(acc[groupKey], log);
    return acc;
  }, {});

  worklogs.forEach(log => {
    if (validParentKeys.has(log.issue_key)) {
      const groupKey = log.issue_key;
      const group = grouped[groupKey];

      if (group) {
        if (!group.parentType) {
          group.parentType = parentInfoMap.get(groupKey)?.type || log.issue_type || null;
        }
        
        addWorklogToGroup(group, log);
      }
    }
  });

  const noParentLogs = worklogs.filter(log =>
    shouldIncludeInNoParentGroup(log, parentKeys, parentInfoMap, validParentKeys)
  );

  if (noParentLogs.length > 0) {
    noParentLogs.forEach(log => {
      const groupKey = log.issue_key;
      const groupLabel = `${groupKey}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = createEmptyGroup(groupLabel, log);
        grouped[groupKey].parentType = null;
        grouped[groupKey].isStandaloneIssue = true;
      }

      addWorklogToGroup(grouped[groupKey], log);
    });
  }

  return finalizeGroups(grouped);
};


